import styled from 'styled-components';
import * as React from 'react';

type MountFn<TMountProps> = (el: HTMLDivElement, props: TMountProps) => void;

interface MountProps<TMountProps> {
  containerId: string;
  mount: MountFn<TMountProps>;
  mountProps: TMountProps;
}

const StyledDiv = styled.div`
  margin-left: 24px;
  position: absolute;
`;

/**
 * Creates a div and mounts content within it by calling the provided mount function.
 *
 * Generic over the props forwarded to the remote: the caller supplies both
 * `mount` and `mountProps`, so making this one type parameter is what ties them
 * together. Previously both were `any`, so a caller could pass props the remote's
 * own `mount` signature did not accept and nothing would report it.
 *
 * @param containerId {string} The id to be assigned to the container div.
 * @param mount {MountFn} A function that mounts DOM elements within the provided container.
 * @param mountProps Props to be passed to the mount function.
 * @constructor Creates a Mount react component.
 */
const FederatedMount = <TMountProps,>({
  containerId,
  mount,
  mountProps,
}: MountProps<TMountProps>) => {
  // Typed explicitly: bare `useRef(null)` infers RefObject<null>, so `ref.current`
  // was `null` rather than `HTMLDivElement | null`. That is what made the mount
  // call below require a @ts-ignore.
  const ref = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    // Effects run after the DOM is committed, so the ref is populated and this
    // guard does not trip in practice. It replaces a @ts-ignore that was
    // suppressing a real signature mismatch rather than a spurious one: `mount`
    // requires an element, and the old ref type could only ever be null.
    if (!ref.current) return;
    mount(ref.current, mountProps);
  }, [mountProps]);

  return <StyledDiv id={containerId} ref={ref} />;
};

export default FederatedMount;
